import React, { useRef, useState } from "react";
import { brand } from "./constants";

interface ControlsProps {
  mode: string;
  exportScale: number;
  solidBG: boolean;
  showLatex: boolean;
  modes: [string, string][];
  onPrint: () => void;
  onExportSVG: () => void;
  onExportPNG: () => void;
  onExportWebP: () => void;
  onCopyPNG: () => void;
  onCopySVG: () => void;
  onScaleChange: (scale: number) => void;
  onSolidBGChange: (checked: boolean) => void;
  onToggleLatex: () => void;
  onModeChange: (mode: string) => void;
}

export function Controls({
  mode,
  exportScale,
  solidBG,
  showLatex,
  modes,
  onPrint,
  onExportSVG,
  onExportPNG,
  onExportWebP,
  onCopyPNG,
  onCopySVG,
  onScaleChange,
  onSolidBGChange,
  onToggleLatex,
  onModeChange,
}: ControlsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const scrollByAmount = (dx: number) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 text-white/80 no-print">
      <button
        onClick={onPrint}
        className="px-4 py-2 rounded-2xl shadow bg-white/90 hover:bg-white text-neutral-800 text-sm font-semibold tracking-wide"
      >
        Print / PDF
      </button>

      {/* Compact export menu */}
      <div className="export-menu-container">
        <button
          onClick={() => setShowExportMenu(!showExportMenu)}
          className="px-3 py-2 rounded-md text-xs font-bold border bg-black/30 text-white hover:bg-black/50"
          style={{ borderColor: "#444" }}
        >
          Export ⬇
        </button>
        
        {showExportMenu && (
          <div className="export-menu-dropdown">
            <button onClick={() => { onExportSVG(); setShowExportMenu(false); }} className="export-menu-item">
              SVG
            </button>
            <button onClick={() => { onExportPNG(); setShowExportMenu(false); }} className="export-menu-item">
              PNG {exportScale}×
            </button>
            <button onClick={() => { onExportWebP(); setShowExportMenu(false); }} className="export-menu-item">
              WebP {exportScale}×
            </button>
            <button onClick={() => { onCopyPNG(); setShowExportMenu(false); }} className="export-menu-item">
              Copy PNG
            </button>
            <button onClick={() => { onCopySVG(); setShowExportMenu(false); }} className="export-menu-item">
              Copy SVG
            </button>
          </div>
        )}
      </div>

      {/* Scale & options */}
      <div className="flex items-center gap-2 text-xs">
        <label className="flex items-center gap-1">
          <span>Scale:</span>
          <select
            value={exportScale}
            onChange={(e) => onScaleChange(parseInt(e.target.value))}
            className="bg-black/40 border border-white/30 rounded px-2 py-1"
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}×
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={solidBG} onChange={(e) => onSolidBGChange(e.target.checked)} /> BG
        </label>
        <button
          onClick={onToggleLatex}
          className="px-3 py-1 rounded-md text-xs font-bold border bg-black/30 text-white"
          style={{ borderColor: "#444" }}
        >
          LaTeX
        </button>
      </div>
      {/* Mode selector - stylish scrollable chips with glow */}
      <div className="mode-strip-wrap">
        <button
          type="button"
          aria-label="Scroll left"
          className="scroll-btn scroll-btn--left"
          onClick={() => scrollByAmount(-320)}
        >
          ‹
        </button>
        <div ref={listRef} className="mode-strip scroll-mask" role="tablist" aria-label="Artistic modes">
          {modes.map(([val, label]) => {
            const active = mode === val;
            return (
              <button
                key={val}
                role="tab"
                aria-selected={active}
                title={label}
                onClick={() => onModeChange(val)}
                className={`mode-chip ${active ? "is-active" : ""}`}
                style={{ borderColor: active ? brand.violet : "#333" }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label="Scroll right"
          className="scroll-btn scroll-btn--right"
          onClick={() => scrollByAmount(320)}
        >
          ›
        </button>
      </div>
      <span className="text-xs uppercase tracking-widest opacity-70">Vasarely+ Optical — Collab Sets</span>
    </div>
  );
}
